import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, DatePipe, NgOptimizedImage } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, viewChild } from '@angular/core';
import { FormControl, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ExtendedProblemDetail, UserRolesEnum } from '@buspro/shared/common';
import { LocalUserDto } from '@buspro/web/apis';
import {
  ErrorDisplayComponent,
  LocalAuthService,
  WEB_CONFIG,
  WebAppEnvironment,
  syncAutofilledInputs,
  useFormErrorHandler,
} from '@buspro/web/core';
import { EnvBannerComponent } from '@buspro/web/shared';
import { NgxAuthService } from '@corelabs/ng-auth';
import { FeInputErrorDirective } from '@fulleffect/ngx-tailings';
import { UntilDestroy } from '@ngneat/until-destroy';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'buspro-admin-login',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    DatePipe,
    ReactiveFormsModule,
    FeInputErrorDirective,
    FormsModule,
    EnvBannerComponent,
    ErrorDisplayComponent,
  ],
  animations: [
    trigger('cardEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(12px) scale(0.98)' }),
        animate('850ms cubic-bezier(0.2, 0, 0, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
    ]),
    trigger('errorExpand', [
      transition(':enter', [
        style({ height: 0, opacity: 0, marginTop: 0 }),
        animate('250ms ease-out', style({ height: '*', opacity: 1, marginTop: '*' })),
      ]),
      transition(':leave', [animate('200ms ease-in', style({ height: 0, opacity: 0, marginTop: 0 }))]),
    ]),
    trigger('shake', [
      state('steady', style({ transform: 'none' })),
      transition('* => *', [
        animate(
          '1800ms cubic-bezier(.36,.07,.19,.97)',
          keyframes([
            style({ transform: 'translateX(0)' }),
            style({ transform: 'translateX(-6px)' }),
            style({ transform: 'translateX(6px)' }),
            style({ transform: 'translateX(-4px)' }),
            style({ transform: 'translateX(4px)' }),
            style({ transform: 'translateX(0)' }),
          ]),
        ),
      ]),
    ]),
  ],
  template: `
    <div
      class="login-bg py-10 lg:py-24  w-screen h-screen justify-center items-center"
    >
      <div
        class="relative flex flex-col lg:flex-row bg-white rounded-lg shadow-2xl overflow-hidden mx-auto max-w-sm lg:max-w-5xl"
        [@cardEnter]
        [@shake]="shakeKey"
      >
        <buspro-env-banner [label]="env"></buspro-env-banner>

        <div
          class="lg:block h-32 lg:h-auto lg:w-1/2 bg-cover"
          style="background-image:url('theme3.jpg')"
        >
          <div
            class="hidden bg-gradient-to-br from-transparent to-blue-900 h-full w-full lg:relative  lg:p-12 lg:flex flex-col-reverse"
          >
            <p class="mt-4 leading-relaxed text-lg font-medium text-white/80">
              For BUSPRO management and employees internal use only.
            </p>

            <h2
              class="mt-6 text-2xl font-bold text-white sm:text-3xl md:text-4xl"
            >
              Administrative Portal 📍
            </h2>
          </div>
        </div>

        <div class="w-full px-12 py-16 lg:w-1/2">
          <div class="w-64 mx-auto">
            <img
              ngSrc="logo-no-border.png"
              alt="Logo"
              height="188"
              width="600"
            />
          </div>
          <h2 class="text-2xl font-semibold text-gray-700 text-center mt-8">
            BACKOFFICE 
          </h2>

          <div class="mt-8 flex items-center justify-between">
            <span class="border-b w-1/5 lg:w-1/4"></span>
            <a href="#" class="text-xs text-center text-gray-500 uppercase"
              >Sign in to continue</a
            >
            <span class="border-b w-1/5 lg:w-1/4"></span>
          </div>

          <!-- Error Display Component -->
          <div class="mt-4" *ngIf="showError" [@errorExpand]>
            <app-error-display
              [error]="errorDetails"
              [showRetry]="true"
              (retry)="onRetryLogin()"
              (dismiss)="onDismissError()"
            />
          </div>

          <form #loginForm [formGroup]="form" (ngSubmit)="onSubmit()">
            <div class="mt-4">
              <label class="block text-gray-700 text-sm font-bold mb-2"
                >Email Address</label
              >
              <input
                autocomplete="username"
                name="email"
                formControlName="email"
                feInputError
                class="bg-gray-200 text-gray-700 focus:outline-none focus:shadow-outline border border-gray-300 rounded py-2 px-4 block w-full appearance-none"
                type="email"
              />
              
            </div>

            <div class="mt-4">
              <div class="flex justify-between">
                <label class="block text-gray-700 text-sm font-bold mb-2"
                  >Password</label
                >
              </div>
              <input
                autocomplete="current-password"
                name="password"
                formControlName="password"
                feInputError
                class="bg-gray-200 text-gray-700 focus:outline-none focus:shadow-outline border border-gray-300 rounded py-2 px-4 block w-full appearance-none"
                type="password"
              />
              
            </div>

            <div class="mt-8">
              <button
                type="submit"
                [disabled]="loginMutation.isPending()"
                class="bg-gray-700 btn btn-primary text-white font-bold py-2 px-4 w-full rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span *ngIf="loginMutation.isPending()" class="inline-block animate-spin mr-2">⏳</span>
                {{ loginMutation.isPending() ? 'Signing In...' : 'Login' }}
              </button>
            </div>
          </form>

          <div class="mt-8 text-gray-400 text-center text-sm">
            Copyright &nbsp; &#64;{{ currentDate | date : 'YYYY' }}. BusPro. All
            rights reserved.
          </div>
          
        </div>
      </div>
    </div>
  `,
  styles: `
    .login-bg {
      background-color: #cccccc;
      background-image: url("/images/admin_login_bg.svg");
      background-attachment: fixed;
      background-size: cover;
    }
  `,
})
export class AdminLoginComponent implements AfterViewInit {
  private readonly authService = inject(LocalAuthService);
  private readonly logger = inject(NGXLogger);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);
  private readonly ngxAuthService = inject(NgxAuthService);
  private readonly router = inject(Router);
  private readonly formErrorHandler = useFormErrorHandler();

  currentDate = new Date();
  shakeKey = 0;

  // Form setup
  private loginFormRef = viewChild<ElementRef<HTMLFormElement>>('loginForm');

  email = new FormControl<string>('', [Validators.required, Validators.email]);
  password = new FormControl<string>('', [Validators.required]);
  form = this.fb.group({
    email: this.email,
    password: this.password,
  });

  // Error state
  showError = false;
  errorDetails: ExtendedProblemDetail | null = null;
  fieldErrors: Record<string, string> = {};

  // Login mutation using TanStack Query
  loginMutation = injectMutation(() => ({
    mutationFn: async (loginData: { email: string; password: string }): Promise<LocalUserDto> => {
      // Convert Observable to Promise for TanStack Query compatibility
      return firstValueFrom(this.ngxAuthService.login(loginData));
    },
    onSuccess: (user: LocalUserDto) => {
      this.logger.log('User logged in successfully', user);
      this.clearErrors();

      // Navigate to appropriate home route based on user role
      const homeRoute = this.authService.homeRoutePerRole(user.role as UserRolesEnum);
      this.logger.log('Navigating user to', homeRoute);

      this.router.resetConfig(this.router.config);
      this.router.navigateByUrl(homeRoute).catch((reason) => {
        this.logger.error('Navigation error:', reason);
      });
    },
    onError: (error: unknown) => {
      this.logger.error('Login failed', error);

      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'admin-login-form',
      });

      if (!result.shouldHandleGlobally) {
        this.showError = true;
        this.errorDetails = result.problemDetail;
      }
      // trigger shake animation on error
      this.shakeKey++;
      // Global errors (network issues, etc.) will be handled by HTTP interceptor
    },
  }));

  get env() {
    return this.environment.env;
  }

  ngAfterViewInit(): void {
    // Page-load autofill lands after the first render, so give the browser a
    // tick and then adopt it — this is what lets the form report itself valid
    // without the user clicking into it first.
    setTimeout(() => {
      syncAutofilledInputs(this.form, this.loginFormRef()?.nativeElement);
    });
  }

  onSubmit() {
    // Clear previous errors
    this.clearErrors();

    // Adopt anything the browser autofilled — Angular does not see a value
    // assigned straight onto the input, so the controls can still be empty
    // while the fields visibly hold the saved credentials.
    syncAutofilledInputs(this.form, this.loginFormRef()?.nativeElement);

    // Validate form
    if (this.form.invalid) {
      this.markFormGroupTouched();
      return;
    }

    // Execute login mutation
    const loginData = {
      email: this.email.value || '',
      password: this.password.value || '',
    };

    this.loginMutation.mutate(loginData);
  }

  onRetryLogin() {
    this.clearErrors();
    this.onSubmit();
  }

  onDismissError() {
    this.clearErrors();
  }

  private markFormGroupTouched() {
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  private clearErrors() {
    this.showError = false;
    this.errorDetails = null;
    this.fieldErrors = {};

    // Clear server errors from form controls
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control?.errors?.['serverError']) {
        const errors = { ...control.errors };
        delete errors['serverError'];
        control.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    });
  }
}
